import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Mail, ArrowRight } from 'lucide-react';

// --- Utility Functions ---

// Regex for Indian phone number (10 digits starting with 6, 7, 8, or 9)
const isValidIndianPhone = (phone: string) =>
  /^[6-9]\d{9}$/.test(phone.replace(/\s|-/g, ''))

// Robust Email Format Checker
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,4}$/.test(email);
};

// --- New Success Component ---
interface EmailConfirmationProps {
  email: string;
}

const EmailConfirmationOverlay: React.FC<EmailConfirmationProps> = ({ email }) => (
  <div className="flex flex-col items-center justify-center p-6 text-center animate-fadeIn">

    {/* Animated Mailbox Icon */}
    <div className="relative mb-6">
      <Mail className="w-20 h-20 text-[#AAD816]" strokeWidth={1.5} />
      <span className="absolute bottom-0 right-0 w-6 h-6 bg-[#AAD816] rounded-full animate-ping opacity-75"></span>
      <span className="absolute bottom-0 right-0 w-6 h-6 bg-zinc-950 rounded-full flex items-center justify-center border-2 border-white">
        <ArrowRight className="w-4 h-4 text-white" />
      </span>
    </div>

    <h2 className="text-3xl font-black text-zinc-950 mt-4 tracking-tight">
      Check Your Inbox!
    </h2>
    <p className="mt-4 text-zinc-600 leading-relaxed font-medium">
      We've sent a confirmation link to:
      <br />
      <strong className="text-zinc-950 font-black text-lg break-all">{email}</strong>
    </p>
    <p className="mt-6 text-xs font-bold text-zinc-400 uppercase tracking-widest">
      Please verify your account to get started
    </p>

    <a
      href={`mailto:${email}`}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-8 w-full bg-zinc-950 hover:bg-[#AAD816] text-white hover:text-zinc-950 font-black py-4 px-6 rounded-2xl transition-all duration-300 shadow-xl shadow-zinc-200"
    >
      Open Email App
    </a>
  </div>
);


// --- Main Component ---

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [formStartTime] = useState(Date.now());
  const [isSuccess, setIsSuccess] = useState(false); // New state for success screen
  const [registeredEmail, setRegisteredEmail] = useState(''); // New state to store email
  const { signUp } = useAuth()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    // Specific handling for phone number
    if (name === 'phoneNumber') {
      // Restrict phone number input to only digits and limit to 10 characters
      newValue = value.replace(/[^0-9]/g, '').slice(0, 10);

      // Validation check for display below the input
      if (newValue.length === 10) {
        setPhoneError(isValidIndianPhone(newValue) ? '' : 'Enter a valid 10-digit number starting with 6, 7, 8, or 9')
      } else if (newValue.length > 0 && newValue.length < 10) {
        setPhoneError('Phone number must be exactly 10 digits.')
      } else {
        setPhoneError('')
      }
    }

    // Default handling for all fields
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    // --- Time-Based Throttling Check ---
    const timeElapsed = Date.now() - formStartTime;
    if (timeElapsed < 2000) {
      toast.error('Registration failed. Please try again.')
      setLoading(false);
      return;
    }

    const { firstName, lastName, email, phoneNumber, password, confirmPassword } = formData

    // --- Pre-submission Validation ---
    if (!firstName || !lastName || !email || !phoneNumber || !password || !confirmPassword) {
      toast.error('Please fill in all fields')
      setLoading(false)
      return
    }

    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address format.')
      setLoading(false)
      return
    }

    if (!isValidIndianPhone(phoneNumber)) {
      setPhoneError('Enter a valid 10-digit Indian phone number')
      toast.error('Please enter a valid phone number.')
      setLoading(false)
      return
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long')
      setLoading(false)
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      setLoading(false)
      return
    }
    // ---------------------------------

    try {
      await signUp(email, password, firstName, lastName, phoneNumber)

      // VVV NEW SUCCESS LOGIC VVV
      setRegisteredEmail(email);
      setIsSuccess(true);
      // Removed: toast.success('Account created! Please check your email to verify your account.') 

      // Reset sensitive fields for security after successful signup
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

    } catch (error: any) {
      console.error('Registration error:', error)
      const errorMessage = error.message.includes('User already registered')
        ? 'A user with this email or phone number already exists.'
        : 'Failed to create account. Please try again.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-3xl shadow-2xl border border-zinc-100">

        {isSuccess ? (
          // Render the confirmation screen on success
          <EmailConfirmationOverlay email={registeredEmail} />
        ) : (
          // Render the registration form otherwise
          <>
            <div>
              <div className="flex justify-center mb-8">
                <img
                  src="/gympe.svg"
                  alt="Gympe"
                  className="h-10 w-auto"
                />
              </div>
              <h2 className="mt-6 text-center text-3xl font-black text-zinc-950 tracking-tight">
                Join Gympe today
              </h2>
              <p className="mt-2 text-center text-sm font-medium text-zinc-500">
                Create your account and start your management journey
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="firstName" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">First name</label>
                    <input id="firstName" name="firstName" type="text" autoComplete="given-name" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium" placeholder="John" disabled={loading} />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="lastName" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Last name</label>
                    <input id="lastName" name="lastName" type="text" autoComplete="family-name" required value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium" placeholder="Doe" disabled={loading} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Email address</label>
                  <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium" placeholder="name@gym.com" disabled={loading} />
                </div>

                {/* --- PHONE NUMBER FIELD --- */}
                <div className="space-y-1.5">
                  <label htmlFor="phoneNumber" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Phone Number</label>
                  <div className="flex bg-zinc-50 rounded-2xl border border-zinc-200 focus-within:ring-2 focus-within:ring-[#AAD816] transition-all duration-300">
                    <div className="flex items-center space-x-2 px-4 py-3 border-r border-zinc-200 text-zinc-500 font-bold select-none">
                      <span aria-hidden="true">🇮🇳</span>
                      <span className="text-sm">+91</span>
                    </div>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      required
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="flex-1 min-w-0 px-4 py-3 bg-transparent focus:outline-none font-medium"
                      placeholder="9876543210"
                      disabled={loading}
                      maxLength={10}
                      inputMode="numeric"
                    />
                  </div>
                  {phoneError && (
                    <p className="text-[10px] font-bold text-red-500 mt-1 ml-1 uppercase">{phoneError}</p>
                  )}
                </div>
                {/* --- END PHONE NUMBER FIELD --- */}

                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Password</label>
                  <input id="password" name="password" type="password" autoComplete="new-password" required value={formData.password} onChange={handleChange} className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium" placeholder="Min 8 characters" disabled={loading} />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="confirmPassword" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Confirm password</label>
                  <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={formData.confirmPassword} onChange={handleChange} className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium" placeholder="Repeat password" disabled={loading} />
                </div>
              </div>

              <div className="flex items-start">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 bg-zinc-50 border-zinc-300 rounded text-zinc-950 focus:ring-[#AAD816]"
                />
                <label htmlFor="terms" className="ml-3 block text-xs font-medium text-zinc-500 leading-normal">
                  I agree to the{' '}
                  <Link to="/terms" className="text-zinc-950 font-bold hover:text-[#AAD816] underline underline-offset-2">
                    Terms
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-zinc-950 font-bold hover:text-[#AAD816] underline underline-offset-2">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-zinc-950 hover:bg-[#AAD816] text-white hover:text-zinc-950 font-black py-4 px-6 rounded-2xl transition-all duration-300 shadow-xl shadow-zinc-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                      Creating Account...
                    </div>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Get Started
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
              <div className="text-center pt-4">
                <span className="text-sm font-medium text-zinc-500">
                  Already a member?{' '}
                  <Link
                    to="/login"
                    className="font-bold text-zinc-950 hover:text-[#AAD816] transition-colors underline-offset-4 hover:underline"
                  >
                    Sign in here
                  </Link>
                </span>
              </div>
            </form>
          </>
        )}
      </div>
    </div >
  )
}
